'use client';

import LLInput from '@next-app/components/elements/LLInput/LLInput';
import LLHeading from '@next-app/components/elements/LLHeading/LLHeading';
import CountryList from './CountryList';
import StateList from './StateList';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import LLPopover from '@next-app/components/elements/LLPopover/LLPopover';
import {
  compareCommonFields,
  getCartItemsForDTMEvent,
  handleCheckoutServerErrors,
  saveDeliveryAddress,
  saveGuestEmailDetails,
  sendOnBlurDTMEvent,
  triggerCheckoutFormErrorEvent,
  validateFields,
} from '@next-app/utils/CheckoutHelper';
import { SaveDeliveryAddressRequest } from '@next-app/interface/Checkout/SaveDeliveryAddress';
import {
  selectCartInfo,
  selectDynSess,
  selectIsFurniture,
  selectIsGuestUser,
  selectIsLoggedIn,
  selectLlmAPIVersion,
  selectUserProfile,
} from '@next-app/lib/features/InitSelectors';
import {
  formatPhoneNumber,
  getUnformattedNumber,
} from '@next-app/utils/form-validation';
import { useDispatch, useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import { LoqateApiAddress } from '@next-app/interface/AddressAutoFill';
import {
  containsEmptyValues,
  containsSomeEmptyValues,
  isNotEmptyObject,
  isNotNullOrUndefinedorEmpty,
  showDynamicModal,
} from '@next-app/utils/common-utils';
import LLSwitch from '@next-app/components/elements/LLSwitch/LLSwitch';
import {
  setLoadCheckoutAdditionalMsg,
  setLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  DeliveryAddressFormFields,
  DeliveryAddressFormInitialState,
} from './DeliveryAddressFormFields';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_ACCEPTED_CHARACTERS,
  FIELD_VALIDATION_RULES,
} from '@next-app/constants/CheckoutConstants';
import {
  AddressInfo,
  CheckoutServerErrorResponse,
  ShippingAddressResponse,
} from '@next-app/lib/features/checkout/Checkout.interface';
import AddressAutoFill from '@next-app/components/shared/AddressAutoFill/AddressAutoFill';
import {
  selectAllProfileAddresses,
  selectCommerceItems,
  selectEmailInfo,
  selectHasItemFromRegistry,
  selectHasItemFromShippingList,
  selectIsCheckoutDetailsLoaded,
  selectIsPureBopisOrder,
  selectIsPureDigitalOrder,
  selectIsShipOrder,
  selectIsShippingAddressCollapsed,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import dynamic from 'next/dynamic';
import {
  IContactErrorFormState,
  IContactFormState,
} from '@next-app/interface/Checkout/ContactDeliverySection';
import { dyEvent } from '@next-app/utils/dy-event';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import Loader from '@next-app/components/shared/Loader/Loader';

const ShowAddressModal = dynamic(
  () => import('@next-app/components/ShowAddressModal/ShowAddressModal'),
  { ssr: true },
);

interface IDeliveryAddressFormProps {
  id: string;
  formHeading?: string;
  showDefaultAddressSwitch: boolean;
  isNewAddress: boolean;
  showContinue: boolean;
  address?: AddressInfo;
  onCancel?: any;
  contactFormState?: IContactFormState;
  setShowCollapse: Function;
  contactFormErrors: IContactErrorFormState;
  setShowAddressModal: Function;
  showAddressModal: boolean;
  setContactFormErrors?: Function;
  onScrollToSection?: () => void;
}

const DeliveryAddressForm: FC<IDeliveryAddressFormProps> = ({
  id,
  formHeading,
  showDefaultAddressSwitch,
  isNewAddress,
  showContinue,
  address,
  onCancel,
  contactFormState,
  setShowAddressModal,
  showAddressModal,
  setShowCollapse,
  onScrollToSection,
  contactFormErrors,
  setContactFormErrors,
}) => {
  // dispatch
  const dispatch = useDispatch();

  // selectors
  const dynSessConf = useSelector(selectDynSess);
  const { userShopFor } = useSelector(selectEmailInfo) || {};
  const isShipOrder = useSelector(selectIsShipOrder) || false;
  const isCheckoutDetailsLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const isLoggedInUser = useSelector(selectIsLoggedIn);
  const addresses = useSelector(selectAllProfileAddresses);
  const isRegistryOrder = useSelector(selectHasItemFromRegistry);
  const isCTLOrder = useSelector(selectHasItemFromShippingList);
  const IsPureBopisOrder = useSelector(selectIsPureBopisOrder) || false;
  const IsPureDigitalOrder = useSelector(selectIsPureDigitalOrder) || false;
  const isShippingAddressCollapsed = useSelector(
    selectIsShippingAddressCollapsed,
  );
  const commerceItems = useSelector(selectCommerceItems);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);
  const isFurniture = useSelector(selectIsFurniture);
  const isGuestUser = useSelector(selectIsGuestUser);
  const profileInfo = useSelector(selectUserProfile);
  const cartInfo = useSelector(selectCartInfo);

  const [loqateAddress, setLoqateAddress] = useState(
    {} as Partial<AddressInfo>,
  );
  // local state
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    // Only use sessionStorage for guest users or when creating a new address
    if (isGuestUser || isNewAddress) {
      if (typeof window !== 'undefined') {
        const saved = sessionStorage.getItem('deliveryAddressFormData');
        if (saved) return JSON.parse(saved);
      }
      return DeliveryAddressFormInitialState;
    } else if (address) {
      // For editing existing address, use provided address prop
      return {
        ...DeliveryAddressFormInitialState,
        ...address,
      };
    }
    return DeliveryAddressFormInitialState;
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        'deliveryAddressFormData',
        JSON.stringify(formData),
      );
    }
  }, [formData]);

  useEffect(() => {
    // Only update from sessionStorage for guest users or new address
    if (isGuestUser || isNewAddress) {
      if (typeof window !== 'undefined') {
        const saved = sessionStorage.getItem('deliveryAddressFormData');
        if (saved) setFormData(JSON.parse(saved));
      }
    } else if (address) {
      setFormData({
        ...DeliveryAddressFormInitialState,
        ...address,
      });
    }
  }, [isGuestUser, isNewAddress, address]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('deliveryAddressFormData');
    }
    // When switching to edit mode, prefill with address
    if (!isNewAddress && address) {
      setFormData({
        ...DeliveryAddressFormInitialState,
        ...address,
      });
    }
  }, [isNewAddress, address]);

  // methods
  /**
   * handleSubmit
   * POSTS form values to ShippingAddress API
   * @param e
   */
  const scrollToErrorField = () => {
    if (formErrors) {
      const firstErrorField = Object.keys(formErrors).find(
        (key) => formErrors[key],
      );
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );
        if (
          errorElement &&
          document.activeElement !== errorElement &&
          !errorElement.contains(document.activeElement)
        ) {
          const rect = errorElement.getBoundingClientRect();
          const isInView =
            rect.top >= 0 &&
            rect.bottom <=
              (window.innerHeight || document.documentElement.clientHeight);

          if (!isInView) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let avsStatus = false;
      if (isNotEmptyObject(loqateAddress)) {
        avsStatus = compareCommonFields(loqateAddress, formData);
      }
      if (isShipOrder) {
        const isFormValid = validateFields(
          formData,
          validateField,
          setFormErrors,
        );
        if (!isFormValid) {
          scrollToErrorField();
          const errors = isNotNullOrUndefinedorEmpty(formErrors)
            ? Object.entries(formErrors as object)
                .filter(([, error]) => error !== '' && error !== null)
                .map(([property, error]) => ({
                  localizedMessage: error,
                  errorCode: property,
                  propertyPath: property,
                }))
            : [];
          if (errors?.length > 0) {
            triggerCheckoutFormErrorEvent({
              step: 'shipping',
              llmAPIVersion,
              isFurniture,
              errors: errors,
            });
          }
          return;
        }
        if (isFormValid) {
          const { active, ...restForm } = formData;
          if (contactFormState?.promoCheckbox) {
            dyEvent({
              eventName: 'Newsletter Subscription',
              type: 'newsletter-subscription-v1',
              email: contactFormState?.email,
            });
          }
          const shippingAddressResponse: ShippingAddressResponse &
            CheckoutServerErrorResponse = await saveDeliveryAddress(
            {
              ...restForm,
              phoneNumber: getUnformattedNumber(formData.phoneNumber as string),
              isCheckoutPage: true,
              isNewAddress,
              emailOptIn: contactFormState?.promoCheckbox,
              _dynSessConf: dynSessConf,
              avsStatus,
              sortProperties: '+lastName,+firstName',
              howMany: -1,
              start: 0,
              ...((isLoggedInUser || addresses !== null) && { active: active }),
              ...((!isLoggedInUser || addresses == null) && {
                isCheckoutAddresses: true,
                useShippingAddress: true,
                email: contactFormState?.email,
                userShopFor: userShopFor || 'all',
                //isSuggestedAddressSelected: true,
                singleLineFullAddress: `${formData.address1}, ${formData.address2}, ${formData.city}, ${formData.state} ${formData.postalCode}`,
                validateContactInfo: true,
                type: 'Commercial',
              }),
              ...(!isNewAddress && {
                isSelectAddress: true,
                addressId: address?.repositoryId,
                oldAddressId: address?.repositoryId,
              }),
            } as SaveDeliveryAddressRequest,
            isFurniture,
          );

          if (isNotEmptyObject(shippingAddressResponse)) {
            if (shippingAddressResponse?.checkoutResponse) {
              const checkoutResponse = shippingAddressResponse.checkoutResponse;
              const message = shippingAddressResponse?.message;
              // trigger dtm event
              const eventData = {
                event: 'checkoutShippingContinue',
                detail: {
                  cart: { items: getCartItemsForDTMEvent(commerceItems) },
                  city: formData?.city,
                  country: formData?.country,
                  email: isGuestUser
                    ? contactFormState?.email
                    : profileInfo?.email,
                  isFurniture: isFurniture,
                  isSuggestedAddressSelected: avsStatus,
                  llmAPIVersion: llmAPIVersion,
                  md5Email: profileInfo?.md5Email,
                  orderId: isGuestUser ? profileInfo?.id : cartInfo?.cartId,
                  singleLineAddress: formData?.displayName,
                  success: true,
                  state: formData?.state,
                  userShopFor: userShopFor || 'all',
                  userSubscribe: contactFormState?.promoCheckbox,
                  zip: formData?.postalCode,
                },
              };
              DynamicDTMEvent(eventData);
              dispatch(
                setLoadCheckoutDetails(
                  shippingAddressResponse?.checkoutResponse,
                ),
              );
              if (message?.hintAddress) {
                setShowAddressModal(true);
                showDynamicModal();
              }
              isShippingAddressCollapsed
                ? setShowCollapse(false)
                : setShowCollapse(true);
            }
            if (shippingAddressResponse?.message) {
              dispatch(
                setLoadCheckoutAdditionalMsg(shippingAddressResponse?.message),
              );
            }
            if (shippingAddressResponse.formError) {
              triggerCheckoutFormErrorEvent({
                step: 'shipping',
                llmAPIVersion,
                isFurniture,
                errors: shippingAddressResponse?.formExceptions,
              });
              handleCheckoutServerErrors(
                shippingAddressResponse.formExceptions,
                setFormErrors,
              );
            }
            if (shippingAddressResponse.formError && setContactFormErrors) {
              handleCheckoutServerErrors(
                shippingAddressResponse.formExceptions,
                setContactFormErrors,
              );
            }
          }
        }
      } else if (!contactFormErrors?.email) {
        const saveGuestDetailsResponse: ShippingAddressResponse &
          CheckoutServerErrorResponse = await saveGuestEmailDetails(
          {
            email: contactFormState?.email,
            emailOptIn: contactFormState?.promoCheckbox,
            userShopFor: userShopFor || 'all',
            _dynSessConf: dynSessConf,
            howMany: -1,
            start: 0,
          },
          isFurniture,
        );

        if (isNotEmptyObject(saveGuestDetailsResponse)) {
          if (saveGuestDetailsResponse?.checkoutResponse) {
            dispatch(
              setLoadCheckoutDetails(
                saveGuestDetailsResponse?.checkoutResponse,
              ),
            );
            // trigger dtm event
            const eventData = {
              event: 'checkoutShippingContinue',
              detail: {
                cart: { items: getCartItemsForDTMEvent(commerceItems) },
                email: isGuestUser
                  ? contactFormState?.email
                  : profileInfo?.email,
                md5Email: profileInfo?.md5Email,
                orderId: isGuestUser ? profileInfo?.id : cartInfo?.cartId,
                userShopFor: userShopFor || 'all',
                userSubscribe: contactFormState?.promoCheckbox,
                isFurniture: isFurniture,
                llmAPIVersion: llmAPIVersion,
                success: true,
              },
            };
            DynamicDTMEvent(eventData);
            setShowCollapse(false);
          }
          if (saveGuestDetailsResponse?.message) {
            dispatch(
              setLoadCheckoutAdditionalMsg(saveGuestDetailsResponse?.message),
            );
          }
        }
      }
    } catch (error) {
      console.error('Error in applying contact and delivery', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * validateField
   * @param name
   * @param value
   * Validates Delivery Address Form fields and returns error message (one field at a time)
   */
  const validateField = (name: string, value: string): string => {
    let errorMessage = '';
    switch (name) {
      case DeliveryAddressFormFields.FIRST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.FIRST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.FIRST_NAME_INVALID;

        break;
      }
      case DeliveryAddressFormFields.LAST_NAME: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.LAST_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.LAST_NAME_INVALID;
        break;
      }
      case DeliveryAddressFormFields.INSTITUTION: {
        if (value && !FIELD_VALIDATION_RULES.INSTITUTION_NAME.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.INSTITUTION_NAME_INVALID;
        break;
      }
      case DeliveryAddressFormFields.ADDRESS_ONE: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.ADDRESS_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.ADDRESS_ONE.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.ADDRESS_INVALID;
        break;
      }
      case DeliveryAddressFormFields.ADDRESS_TWO: {
        if (value && !FIELD_VALIDATION_RULES.ADDRESS_ONE.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.ADDRESS_INVALID;
        break;
      }
      case DeliveryAddressFormFields.STATE: {
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.STATE_REQUIRED;
        break;
      }
      case DeliveryAddressFormFields.CITY: {
        if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.CITY_REQUIRED;
        else if (!FIELD_VALIDATION_RULES.CITY.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.CITY_INVALID;
        break;
      }
      case DeliveryAddressFormFields.POSTAL_CODE: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_REQUIRED;
        else if (formData[DeliveryAddressFormFields.COUNTRY] == 'US') {
          if (!FIELD_VALIDATION_RULES.US_ZIP_CODES.test(value))
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;
        } else if (formData[DeliveryAddressFormFields.COUNTRY] == 'CA') {
          if (!FIELD_VALIDATION_RULES.CA_ZIP_CODES.test(value))
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;
        } else if (!FIELD_VALIDATION_RULES.INTERNATIONAL_ZIP_CODES.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.POSTAL_CODE_INVALID;

        break;
      }
      case DeliveryAddressFormFields.PHONE_NO: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_REQUIRED;
        else if (
          formData[DeliveryAddressFormFields.COUNTRY] == 'US' ||
          formData[DeliveryAddressFormFields.COUNTRY] == 'CA'
        ) {
          if (
            !FIELD_VALIDATION_RULES.US_CA_PHONE.test(
              getUnformattedNumber(value),
            )
          )
            errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
          // } else if (formData[DeliveryAddressFormFields.COUNTRY] == 'CA') {
          //   if (!FIELD_VALIDATION_RULES.US_CA_PHONE.test(value))
          //     errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        } else if (!FIELD_VALIDATION_RULES.INTERNATIONAL_PHONE.test(value))
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.PHONE_NO_INVALID;
        break;
      }
      case DeliveryAddressFormFields.COUNTRY: {
        if (!value)
          errorMessage = CHECKOUT_VALIDATION_MESSAGES.COUNTRY_REQUIRED;
        break;
      }
    }

    return errorMessage;
  };

  /**
   *
   * @param event
   * @param fieldType
   */
  const handleFormUpdates = useCallback(
    (fieldType: DeliveryAddressFormFields, event: ChangeEvent) => {
      let value = (event.target as HTMLInputElement).value;

      switch (fieldType) {
        case DeliveryAddressFormFields.FIRST_NAME: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.FIRST_NAME, '');
          break;
        }
        case DeliveryAddressFormFields.LAST_NAME: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.LAST_NAME, '');
          break;
        }
        case DeliveryAddressFormFields.INSTITUTION: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.INSTITUTION_NAME, '');
          break;
        }
        case DeliveryAddressFormFields.ATTENTION: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ATTENTION_NAME, '');
          break;
        }
        case DeliveryAddressFormFields.ADDRESS_ONE: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ADDRESS_ONE, '');
          break;
        }
        case DeliveryAddressFormFields.ADDRESS_TWO: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ADDRESS_TWO, '');
          break;
        }
        case DeliveryAddressFormFields.CITY: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.CITY, '');
          break;
        }
        case DeliveryAddressFormFields.STATE: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.STATE, '');
          break;
        }
        case DeliveryAddressFormFields.POSTAL_CODE: {
          value = value.replace(FIELD_ACCEPTED_CHARACTERS.ZIP_CODE, '');
          break;
        }
        case DeliveryAddressFormFields.PHONE_NO: {
          value = value.replace(/\D/g, '');
          if (value.length > 15) {
            value = value.slice(0, 15);
          }
          if (formData[DeliveryAddressFormFields.COUNTRY] === 'US') {
            value = formatPhoneNumber(value);
          }
          break;
        }
      }

      setFormErrors((prev) => ({
        ...prev,
        [fieldType]: '',
      }));

      updateFormData({ [fieldType]: value });
    },
    [formData],
  );

  /**
   * updateFormData
   * @param newState
   */
  const updateFormData = (newState: Record<string, any>) => {
    if (newState?.state)
      setFormErrors((prev) => ({
        ...prev,
        [DeliveryAddressFormFields.STATE]: '',
      }));
    return setFormData((prevState) => ({ ...prevState, ...newState }));
  };

  /**
   * handleAutoFillAddress
   * @param address
   */
  const handleAutoFillAddress = (address: LoqateApiAddress) => {
    const newAddressData: Partial<AddressInfo> = {
      [DeliveryAddressFormFields.COUNTRY]: address.CountryIso2,
      [DeliveryAddressFormFields.ADDRESS_ONE]: `${address.BuildingNumber} ${address.Street}`,
      [DeliveryAddressFormFields.ADDRESS_TWO]: address.SubBuilding,
      [DeliveryAddressFormFields.CITY]: address.City,
      [DeliveryAddressFormFields.STATE]: address.ProvinceCode,
      [DeliveryAddressFormFields.POSTAL_CODE]: address.PostalCode,
    };
    validateFields(newAddressData, validateField, setFormErrors);
    updateFormData(newAddressData);
    setLoqateAddress(newAddressData);
  };

  const isContinueDisabled = () => {
    const {
      country,
      firstName,
      lastName,
      address1,
      city,
      state,
      phoneNumber,
      postalCode,
    } = formData;
    if (isShipOrder) {
      const hasFormError = formErrors && containsEmptyValues(formErrors);
      const hasEmailError = !!contactFormErrors.email;
      const hasEmptyValue = containsSomeEmptyValues({
        country,
        firstName,
        lastName,
        address1,
        city,
        state,
        phoneNumber,
        postalCode,
        email: contactFormState?.email,
      });
      return !hasFormError || hasEmptyValue || hasEmailError;
    } else {
      return !contactFormState?.email || !!contactFormErrors?.email;
    }
  };

  const onBlurHandler = (
    key: string,
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const errorMessage = validateField(key, value);
    if (errorMessage) {
      setFormErrors((prev) => ({ ...prev, [key]: errorMessage }));
    }
    sendOnBlurDTMEvent({ key, errorMessage, value });
  };

  const handleButtonClick = () => {
    if (onScrollToSection) {
      onScrollToSection();
    }
    const emailInput = document.querySelector('[name="email"]');
    if (contactFormErrors.email && emailInput) {
      emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Conditional
          if={
            isShipOrder &&
            !(
              !isLoggedInUser &&
              (isRegistryOrder || isCTLOrder) &&
              (IsPureBopisOrder || IsPureDigitalOrder)
            )
          }
        >
          <>
            <Conditional if={isNotNullOrUndefinedorEmpty(formHeading)}>
              <div className="d-flex justify-content-between mb-3">
                <LLHeading tagName="2" className="7">
                  {formHeading}
                </LLHeading>
              </div>
            </Conditional>
            <div className="row mb-3 g-3">
              <div className="col-12">
                <CountryList
                  updateHandler={updateFormData}
                  country={formData?.country || ''}
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(DeliveryAddressFormFields.COUNTRY, e)
                  }
                  automationId="co_shp_country"
                />
              </div>
            </div>
            <div className="row mb-3 g-3">
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="First Name"
                  name="firstName"
                  value={formData[DeliveryAddressFormFields.FIRST_NAME]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.FIRST_NAME]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.FIRST_NAME, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.FIRST_NAME, e)
                  }
                  maxLength={35}
                  automationId="co_shp_first_name"
                />
              </div>
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="Last Name"
                  name="lastName"
                  value={formData[DeliveryAddressFormFields.LAST_NAME]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.LAST_NAME]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.LAST_NAME, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.LAST_NAME, e)
                  }
                  maxLength={35}
                  automationId="co_shp_last_name"
                />
              </div>
            </div>
            <div className="row mb-3 g-3">
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="School/Institution (optional)"
                  value={formData[DeliveryAddressFormFields.INSTITUTION]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.INSTITUTION, e)
                  }
                  errorMsg={formErrors?.[DeliveryAddressFormFields.INSTITUTION]}
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.INSTITUTION, e)
                  }
                  maxLength={50}
                  automationId="co_shp_company"
                />
              </div>
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="Attention (optional)"
                  value={formData[DeliveryAddressFormFields.ATTENTION]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.ATTENTION, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.ATTENTION, e)
                  }
                  maxLength={80}
                  automationId="co_shp_attention"
                />
              </div>
            </div>
            <div className="row mb-3 g-3">
              <div className="col-12 col-md-6">
                <AddressAutoFill
                  idSuffix="delAddrAutoFill"
                  placeholder="Address 1"
                  name="address1"
                  value={formData[DeliveryAddressFormFields.ADDRESS_ONE]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.ADDRESS_ONE]}
                  country={formData[DeliveryAddressFormFields.COUNTRY]}
                  onUpdate={(address) => {
                    handleAutoFillAddress(address);
                  }}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.ADDRESS_ONE, e)
                  }
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(DeliveryAddressFormFields.ADDRESS_ONE, e)
                  }
                  automationId="co_shp_address1"
                />
              </div>
              <div className="col-12 col-md-6">
                <LLInput
                  placeholder="Address 2 (optional)"
                  value={formData[DeliveryAddressFormFields.ADDRESS_TWO]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.ADDRESS_TWO]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.ADDRESS_TWO, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.ADDRESS_TWO, e)
                  }
                  maxLength={40}
                  automationId="co_shp_address2"
                />
              </div>
            </div>
            <div className="row mb-3 g-3">
              <div className="col-12 col-md-4">
                <LLInput
                  placeholder="City"
                  name="city"
                  value={formData[DeliveryAddressFormFields.CITY]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.CITY]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.CITY, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.CITY, e)
                  }
                  maxLength={30}
                  automationId="co_shp_city"
                />
              </div>
              <div className="col-12 col-md-4">
                <StateList
                  updateHandler={updateFormData}
                  country={formData.country}
                  name="state"
                  value={formData[DeliveryAddressFormFields.STATE]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.STATE]}
                  onBlurHandler={(e: React.FocusEvent<HTMLInputElement>) =>
                    onBlurHandler(DeliveryAddressFormFields.STATE, e)
                  }
                  automationId="co_shp_us_state"
                />
              </div>
              <div className="col-12 col-md-4">
                <LLInput
                  placeholder="Zip"
                  value={formData[DeliveryAddressFormFields.POSTAL_CODE]}
                  name="postalCode"
                  errorMsg={formErrors?.[DeliveryAddressFormFields.POSTAL_CODE]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.POSTAL_CODE, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.POSTAL_CODE, e)
                  }
                  maxLength={10}
                  automationId="co_shp_postal_code"
                />
              </div>
            </div>
            <div className="row mb-3 g-3 position-relative">
              <div className="col-12">
                <LLInput
                  placeholder="Phone Number"
                  name="phoneNumber"
                  value={formData[DeliveryAddressFormFields.PHONE_NO]}
                  errorMsg={formErrors?.[DeliveryAddressFormFields.PHONE_NO]}
                  onChange={(e) =>
                    handleFormUpdates(DeliveryAddressFormFields.PHONE_NO, e)
                  }
                  onBlur={(e) =>
                    onBlurHandler(DeliveryAddressFormFields.PHONE_NO, e)
                  }
                  automationId="co_shp_phone"
                />
              </div>
              <div className="position-absolute w-auto input-icon">
                <LLPopover
                  text="Your phone number is required in case we need to contact you about your order."
                  position="top"
                >
                  <span className="ls-icon d-block icn-help-otl"></span>
                </LLPopover>
              </div>
            </div>
          </>
        </Conditional>
        <div
          className={`d-md-flex ${
            showDefaultAddressSwitch &&
            address?.active !== true &&
            !(isGuestUser && (isRegistryOrder || isCTLOrder))
              ? 'justify-content-between'
              : 'justify-content-end'
          }`}
        >
          <Conditional
            if={
              showDefaultAddressSwitch &&
              address?.active !== true &&
              !(isGuestUser && (isRegistryOrder || isCTLOrder))
            }
          >
            <LLSwitch
              id={`toggleDefault${id}`}
              onChange={(e) => {
                updateFormData({
                  [DeliveryAddressFormFields.IS_DEFAULT]: (
                    e.target as HTMLInputElement
                  ).checked,
                });
              }}
              value={formData[DeliveryAddressFormFields.IS_DEFAULT]}
              checked={formData[DeliveryAddressFormFields.IS_DEFAULT] === true}
            >
              Make Default
            </LLSwitch>
          </Conditional>

          <div className="d-flex gap-ll-3 gap-md-2 mt-ll-5 mt-md-0">
            <Conditional if={!showContinue}>
              <>
                <LLButton
                  type="reset"
                  variation="tertiary"
                  onClick={() => {
                    sessionStorage.removeItem('deliveryAddressFormData');
                    if (onCancel) onCancel();
                  }}
                  customClass={'flex-grow-1'}
                  automationId="co_shipping_cancel_button"
                >
                  Cancel
                </LLButton>
                <LLButton
                  type="submit"
                  variation="primary"
                  customClass={'flex-grow-1'}
                  onClick={handleButtonClick}
                  automationId="co_shipping_edit_button"
                >
                  Save
                </LLButton>
              </>
            </Conditional>

            <Conditional if={showContinue}>
              <LLButton
                type="submit"
                variation="secondary"
                customClass="col-12 col-md-auto fs-base lh-base mt-md-ll-3 py-2"
                onClick={handleButtonClick}
                automationId="co_shp_continue"
              >
                Continue
              </LLButton>
            </Conditional>
          </div>
        </div>
      </form>
      {/* Loading Indicator */}
      <Conditional if={loading}>
        <Loader aria-label="Applying Shipping Details" />
      </Conditional>
    </>
  );
};

export default DeliveryAddressForm;
